import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  changeUserConfirmAdminService,
  changeUserPasswordAdminService,
  createUserAdminService,
  deleteUserAdminService,
  editUserAdminService,
  getUsersAdminService,
} from "./../../../services/adminServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const userAdminContext = createContext({
  users: [],
  setUsers: () => {},
  userId: "",
  setUserId: () => {},
  firstName: "",
  setFirstName: () => {},
  lastName: "",
  setLastName: () => {},
  phoneNumber: "",
  setPhoneNumber: () => {},
  role: "",
  setRole: () => {},
  password: "",
  setPassword: () => {},
  confirmPassword: "",
  setConfirmPassword: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleCreateUser: () => {},
  handleEditUser: () => {},
  handleChangeUserPassword: () => {},
  handleChangeUserConfirm: () => {},
  handleDeleteUser: () => {},
});

const UserAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val) => {
    setIsShowModal(true);

    if (val) {
      setUserId(val._id);
      setFirstName(val.firstName);
      setLastName(val.lastName);
      setPhoneNumber(val.phoneNumber);
      setRole(val.role);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setUserId({});
    setFirstName("");
    setLastName("");
    setPhoneNumber("");
    setRole("");
    setPassword("");
    setConfirmPassword("");
  };

  // Get Users
  const handleGetUsers = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService();
      if (status === 200) {
        setUsers(data.result?.users);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create User
  const handleCreateUser = async (event) => {
    event?.preventDefault();

    const objData = {
      firstName,
      lastName,
      phoneNumber,
      role,
      password,
      confirmPassword,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createUserAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit User
  const handleEditUser = async (event) => {
    event?.preventDefault();

    const objData = {
      id: userId,
      firstName,
      lastName,
      phoneNumber,
      role,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editUserAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Change User Password
  const handleChangeUserPassword = async () => {
    const objData = {
      id: userId,
      password,
      confirmPassword,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeUserPasswordAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
        setUserId("");
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Change User Confirm
  const handleChangeUserConfirm = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await changeUserConfirmAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete User
  const handleDeleteUser = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteUserAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetUsers();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <userAdminContext.Provider
      value={{
        users,
        setUsers,
        userId,
        setUserId,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        phoneNumber,
        setPhoneNumber,
        role,
        setRole,
        password,
        setPassword,
        confirmPassword,
        setConfirmPassword,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleCreateUser,
        handleEditUser,
        handleChangeUserPassword,
        handleChangeUserConfirm,
        handleDeleteUser,
      }}
    >
      {children}
    </userAdminContext.Provider>
  );
};

export default UserAdminContext;
